/**
 * Circuit Breaker Banner Component
 * 
 * Shows a banner when backend is degraded (circuit breaker open).
 * Automatically polls for recovery and dismisses when service is restored.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { onCircuitStateChange, apiClient } from '../utils/apiClient';

const CircuitBreakerBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [checking, setChecking] = useState(false);
  
  useEffect(() => {
    // Listen for circuit state changes
    const unsubscribe = onCircuitStateChange((state) => {
      setIsOpen(state.isOpen);
      if (!state.isOpen) {
        setDismissed(false); // Reset dismiss when circuit closes
      }
    });
    
    return unsubscribe;
  }, []);
  
  const handleRetry = async () => {
    setChecking(true);
    try {
      // Try a health check
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthz`);
      if (response.ok) {
        apiClient.resetCircuit();
      }
    } catch (e) {
      // Still failing
    } finally {
      setChecking(false);
    }
  };
  
  if (!isOpen || dismissed) {
    return null;
  }
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 shadow-lg"
      role="alert"
      data-testid="circuit-breaker-banner"
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5" />
          <div>
            <span className="font-medium">Service dégradé</span>
            <span className="ml-2 text-amber-100">
              Le serveur ne répond pas correctement. Nous réessayons automatiquement.
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleRetry}
            disabled={checking}
            className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
            Réessayer
          </button>
          
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-amber-600 rounded transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CircuitBreakerBanner;
